import { create } from 'zustand'
import type { Article } from '@/modules/news/model/types'
import { articlesApi, type AdminArticleDTO } from '@/modules/articles-api'

export interface AdminArticle extends Article {
  content?: string
  status: 'draft' | 'published'
}

interface ArticlesState {
  articles: AdminArticle[]
  loaded: boolean
  loading: boolean
  error: string | null

  fetchAll: () => Promise<void>
  add: (a: Omit<AdminArticle, 'id'>) => Promise<AdminArticle>
  update: (id: string, patch: Partial<AdminArticle>) => Promise<void>
  remove: (id: string) => Promise<void>
  getById: (id: string) => AdminArticle | undefined
  reset: () => Promise<void>
}

export const useAdminArticlesStore = create<ArticlesState>()((set, get) => ({
  articles: [],
  loaded: false,
  loading: false,
  error: null,

  fetchAll: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const page = await articlesApi.adminList({ page: 1, pageSize: 200 })
      const items = Array.isArray(page) ? page : (page?.items ?? []); set({ articles: items as AdminArticle[], loaded: true, loading: false })
    } catch (err: any) {
      console.warn('[articles] fetch failed:', err?.message); set({ articles: [], loaded: true, loading: false, error: err?.message ?? 'Không tải được bài viết' })
    }
  },

  add: async (a) => {
    const created = await articlesApi.adminCreate(a as Omit<AdminArticleDTO, 'id'>)
    const full = created as AdminArticle
    set({ articles: [full, ...get().articles] })
    return full
  },

  update: async (id, patch) => {
    const updated = await articlesApi.adminUpdate(id, patch as Partial<AdminArticleDTO>)
    set({ articles: get().articles.map((x) => (x.id === id ? (updated as AdminArticle) : x)) })
  },

  remove: async (id) => {
    await articlesApi.adminDelete(id)
    set({ articles: get().articles.filter((x) => x.id !== id) })
  },

  getById: (id) => get().articles.find((x) => x.id === id),

  reset: async () => {
    // Backend không có reset — refetch
    await get().fetchAll()
  },
}))
